import { useEffect, type RefObject } from "react";

/**
 * Closes a popover when the user clicks outside its container or presses Escape.
 *
 * @param ref         Container that defines the "inside" region.
 * @param isActive    Effect is a no-op when false (typical: only attach listeners while open).
 * @param onClose     Called on outside mousedown or Escape keydown.
 * @param triggerRef  Optional. If provided, clicks inside the trigger are also treated as
 *                    "inside" so the trigger's own onClick toggle isn't immediately undone.
 */
export function useClickOutside(
    ref: RefObject<HTMLElement>,
    isActive: boolean,
    onClose: () => void,
    triggerRef?: RefObject<HTMLElement>
): void {
    useEffect(() => {
        if (!isActive) return;
        const handleMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (triggerRef?.current?.contains(target)) return;
            if (ref.current?.contains(target)) return;
            onClose();
        };
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("keydown", handleKey);
        };
    }, [ref, isActive, onClose, triggerRef]);
}
